import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, SectionCard, LoadingScreen, EmptyState, DeadlineChip } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const ROLE_COLORS = {
  student: { bg: 'rgba(16,185,129,0.2)', text: '#6ee7b7', label: 'Student' },
  faculty: { bg: 'rgba(99,102,241,0.2)', text: '#a5b4fc', label: 'Faculty' },
};

function ComplaintTable({ complaints, s, navigate }) {
  if (!complaints.length) return <EmptyState text="No complaints in this category" sub="Nothing to review here" />;
  return (
    <table style={s.table}>
      <thead style={s.thead}>
        <tr>
          <th style={s.th}>Complaint</th>
          <th style={s.th}>Filed By</th>
          <th style={s.th}>Submitted</th>
          <th style={s.th}>Votes</th>
          <th style={s.th}>Deadline</th>
          <th style={s.th}>Action</th>
        </tr>
      </thead>
      <tbody>
        {complaints.map(c => (
          <tr key={c.id} className="ur-row-hover" onClick={() => navigate(`/admin/verify/${c.id}`)}>
            <td style={s.tdBold}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {c.is_overdue && <span style={{ width: 7, height: 7, borderRadius: '50%', background: COLORS.red, flexShrink: 0, boxShadow: `0 0 6px ${COLORS.red}` }} />}
                {c.grace_period_notified && !c.is_overdue && <span style={{ width: 7, height: 7, borderRadius: '50%', background: COLORS.amber, flexShrink: 0, boxShadow: `0 0 6px ${COLORS.amber}` }} />}
                <span style={{ color: c.is_overdue ? '#fca5a5' : c.grace_period_notified ? '#fcd34d' : '#fff' }}>{c.title}</span>
              </div>
              {c.grace_period_notified && !c.is_overdue && (
                <div style={{ fontSize: 11, color: '#fcd34d', marginTop: 2, fontWeight: 600 }}>
                  ⚠️ Grace period — auto-escalates soon
                </div>
              )}
              {c.description && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{c.description.slice(0, 60)}{c.description.length > 60 ? '...' : ''}</div>}
            </td>
            <td style={s.td}><span style={{ fontSize: 12 }}>{c.student_name || '—'}</span></td>
            <td style={s.td}><span style={{ fontSize: 12 }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</span></td>
            <td style={s.td}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600,
                color: c.upvotes > 0 ? '#c4b5fd' : 'rgba(255,255,255,0.4)' }}>
                ▲ {c.upvotes || 0}
              </span>
            </td>
            <td style={s.td}><DeadlineChip seconds={c.time_left_seconds} /></td>
            <td style={s.td}>
              <button className="ur-btn-hover" style={{ ...s.btnPrimary, padding: '5px 14px', fontSize: 12 }}>Review</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PendingComplaintsInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [tab, setTab] = useState('student');

  const fetchComplaints = useCallback(async () => {
    try {
      const res = await api.get('/admin/pending');
      setComplaints(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load complaints');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  if (loading) return <LoadingScreen />;

  const studentComplaints = complaints.filter(c => c.submitter_role === 'student');
  const facultyComplaints = complaints.filter(c => c.submitter_role === 'faculty');
  const overdue = complaints.filter(c => c.is_overdue);
  const gracePeriod = complaints.filter(c => c.grace_period_notified && !c.is_overdue);

  const tabStyle = (active, role) => {
    const rc = ROLE_COLORS[role];
    return {
      padding: '6px 18px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
      border: `1px solid ${active ? rc.text : 'rgba(255,255,255,0.15)'}`,
      background: active ? rc.bg : 'rgba(255,255,255,0.05)',
      color: active ? rc.text : 'rgba(255,255,255,0.55)',
      transition: 'all 0.2s',
    };
  };

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar collapsed={false} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Pending Complaints"
            subtitle="Awaiting your review — 24h deadline applies"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={fetchComplaints}>
                Refresh
              </button>
            }
          />
          <div style={s.content}>
            <PageHeader
              icon="⏳"
              title="Pending Complaints"
              subtitle="Awaiting your review — 24h deadline applies"
              stats={[
                { label: 'Total Pending', value: complaints.length },
                { label: 'Students', value: studentComplaints.length },
                { label: 'Faculty', value: facultyComplaints.length },
                { label: 'Overdue', value: overdue.length },
              ]}
            />
            {error && <div style={s.alertErr}>{error}</div>}
            {gracePeriod.length > 0 && (
              <div style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
                borderRadius: 10, padding: '10px 16px', marginBottom: 12, color: '#fcd34d', fontSize: 13, fontWeight: 600 }}>
                ⚠️ {gracePeriod.length} complaint{gracePeriod.length > 1 ? 's are' : ' is'} in grace period — review now to avoid auto-escalation
              </div>
            )}
            {overdue.length > 0 && (
              <div style={{ ...s.alertErr, marginBottom: 16 }}>
                {overdue.length} complaint{overdue.length > 1 ? 's are' : ' is'} overdue — immediate action required
              </div>
            )}

            <SectionCard
              title="Pending Complaints"
              subtitle="Click any complaint to review and verify"
              delay="0.1s"
              action={
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={tabStyle(tab === 'student', 'student')} onClick={() => setTab('student')}>
                    Students ({studentComplaints.length})
                  </button>
                  <button style={tabStyle(tab === 'faculty', 'faculty')} onClick={() => setTab('faculty')}>
                    Faculty ({facultyComplaints.length})
                  </button>
                </div>
              }
            >
              <ComplaintTable
                complaints={tab === 'student' ? studentComplaints : facultyComplaints}
                s={s}
                navigate={navigate}
              />
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PendingComplaints() {
  return <ThemeProvider><PendingComplaintsInner /></ThemeProvider>;
}